import React, { useCallback, useEffect, useRef, useState } from "react";
import { useInView } from "react-intersection-observer";
import { getReports } from "../../api/sanity";
import styles from "../../css/donationCounter.module.css";

const ANIMATION_DURATION_MS = 2600;

function getReportTotal(report) {
  return (
    (report.produce || 0) +
    (report.meat || 0) +
    (report.dairy || 0) +
    (report.grocerySelfStable || 0) +
    (report.baked || 0) +
    (report.prepared || 0) +
    (report.riceAndBeans || 0) +
    (report.gleanersProduce || 0)
  );
}

function getTotalPounds(reports) {
  return reports.reduce((sum, report) => sum + getReportTotal(report), 0);
}

function easeOutExpo(t) {
  return t === 1 ? 1 : 1 - Math.pow(2, -10 * t);
}

const DonationCounter = () => {
  const [totalPounds, setTotalPounds] = useState(0);
  const [loading, setLoading] = useState(true);
  const [displayPounds, setDisplayPounds] = useState(0);
  const [animationComplete, setAnimationComplete] = useState(false);

  const animationRef = useRef(null);
  const hasStartedRef = useRef(false);

  const prefersReducedMotion =
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const [sectionRef, inView] = useInView({
    threshold: 0.3,
    triggerOnce: true,
    rootMargin: "-40px",
  });

  useEffect(() => {
    async function fetchData() {
      try {
        const reports = await getReports();
        setTotalPounds(getTotalPounds(reports));
      } catch (error) {
        console.error("Error fetching donation data:", error);
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, []);

  const runAnimation = useCallback(() => {
    if (totalPounds <= 0) {
      setAnimationComplete(true);
      return;
    }

    const startTime = performance.now();

    const animate = (now) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / ANIMATION_DURATION_MS, 1);
      const eased = easeOutExpo(progress);

      setDisplayPounds(Math.round(eased * totalPounds));

      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        setDisplayPounds(totalPounds);
        setAnimationComplete(true);
      }
    };

    animationRef.current = requestAnimationFrame(animate);
  }, [totalPounds]);

  useEffect(() => {
    if (!inView || loading || totalPounds === 0 || hasStartedRef.current) {
      return;
    }

    hasStartedRef.current = true;

    if (prefersReducedMotion) {
      setDisplayPounds(totalPounds);
      setAnimationComplete(true);
      return;
    }

    runAnimation();

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [inView, loading, totalPounds, prefersReducedMotion, runAnimation]);

  if (loading) {
    return (
      <section className={styles.section}>
        <div className={styles.loading}>Loading donation history…</div>
      </section>
    );
  }

  if (totalPounds === 0) {
    return null;
  }

  const formattedPounds = displayPounds.toLocaleString();

  return (
    <section
      className={styles.section}
      ref={sectionRef}
      aria-labelledby="donation-counter-heading"
    >
      <div
        className={`${styles.counterCard} ${animationComplete ? styles.counterCardDone : ""}`}
        role="region"
        aria-live="polite"
        aria-atomic="true"
        aria-label="Total pounds of food donated"
      >
        <div className={styles.introIcon} aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
          </svg>
        </div>

        <p className={styles.eyebrow} id="donation-counter-heading">
          Since 2024
        </p>
        <p className={styles.subheading}>
          Roslindale Food Collective has donated
        </p>

        <div className={styles.countWrap}>
          <span
            className={`${styles.count} ${animationComplete ? styles.countDone : ""}`}
            aria-hidden="true"
          >
            {formattedPounds}
          </span>
          <span className={styles.suffix}>lbs</span>
        </div>

        <p className={styles.srOnly}>
          Since 2024, Roslindale Food Collective has donated {totalPounds.toLocaleString()} pounds of food
         </p>
      </div>
    </section>
  );
};

export default DonationCounter;
